/**
 * Recipe-line editor for a dish's `dish_ingredients`. Operator
 * selects ingredients from the catalog, enters per-portion qty +
 * unit + optional prep note, and saves the whole batch via
 * `PUT /catalog/dishes/:id/recipe`. Backend pre-validates every
 * line atomically (issue #18) — on validation failure the toast
 * surfaces the per-line reasons returned in the ADR-0006 envelope's
 * `error.extras.reasons` (HTTP 422)
 * with the discriminated kinds (`ingredient_missing`,
 * `unit_mismatch` with expected/got, `duplicate`) so the operator
 * does not have to whack-a-mole one row at a time.
 */
import { useState, useMemo, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Button,
  Input,
  Loading,
} from '@resource-kit/ui/react';
import { useToast } from '@components/ui/Toast';
import { dishAPI, ingredientAPI, type Dish, type Ingredient } from '@/services';
import { CANONICAL_UNITS, type CanonicalUnit } from '@/services/catalog-enums';
import { ApiError } from '@lib/api';
import { resolveApiError } from '@lib/errors';

interface Props {
  dish: Dish;
}

interface RecipeRow {
  /**
   * Stable per-row identity that survives re-renders, deletions, and
   * mid-list inserts so React reconciliation does not re-use DOM
   * nodes between unrelated rows (would otherwise jump cursor/focus
   * when the operator removes a row above the one they are typing
   * in). Generated client-side; backend ignores it on save.
   */
  clientId: string;
  ingredient_id: string;
  qty_per_portion: string;
  /** Closed-set unit per backend `dish_ingredients.unit` column
   *  (`CanonicalUnit` from `@/services/catalog-enums`). Empty string
   *  represents "not yet picked"; `buildSavePayload` rejects rows
   *  with an empty unit per the existing skip-or-save contract. */
  unit: CanonicalUnit | '';
  prep_note: string;
}

let rowSeq = 0;
const nextClientId = () => `r${++rowSeq}`;

const newRow = (): RecipeRow => ({
  clientId: nextClientId(),
  ingredient_id: '',
  qty_per_portion: '',
  unit: '',
  prep_note: '',
});

// Mirrors backend `routes/catalog/ingredients.ts` listQuerySchema cap
// + `IngredientService.findMany` LIST_HARD_CAP. Centralised so a
// future bump is a one-line change in lockstep with the backend.
const CATALOG_LIST_CAP = 100;

/**
 * Discriminated reason union from backend `setRecipe` →
 * `error.extras.reasons` (see
 * `backend/src/contexts/kitchen/app/catalog.ts` LineReason). Mirrored
 * here so the editor renders per-line detail without cross-package
 * type coupling.
 *
 * Drift guard: the formatter's switch is exhaustive against this
 * union; adding a new backend reason kind without updating the union
 * here would compile against the upstream type but the format
 * function's `_exhaustive: never` line would catch it locally.
 */
type InvalidLineReason =
  | { ingredient_id: string; reason: 'ingredient_missing' }
  | { ingredient_id: string; reason: 'unit_mismatch'; expected: string; got: string }
  | { ingredient_id: string; reason: 'duplicate' };

function formatInvalidLines(
  reasons: ReadonlyArray<InvalidLineReason>,
  ingredientById: Map<string, Ingredient>,
  t: (k: string, opts?: Record<string, unknown>) => string,
): string {
  const labelFor = (id: string) => ingredientById.get(id)?.name ?? id;
  return reasons
    .map((r) => {
      switch (r.reason) {
        case 'ingredient_missing':
          return t('dish.recipe.error.line.ingredientMissing', {
            ingredient: labelFor(r.ingredient_id),
          });
        case 'unit_mismatch':
          return t('dish.recipe.error.line.unitMismatch', {
            ingredient: labelFor(r.ingredient_id),
            expected: r.expected,
            got: r.got,
          });
        case 'duplicate':
          return t('dish.recipe.error.line.duplicate', { ingredient: labelFor(r.ingredient_id) });
        default: {
          // Exhaustiveness guard — adding a new reason kind to the
          // union forces this line to fail to compile (the new kind
          // is no longer assignable to `never`). Drift-prevent in
          // the spirit of CLAUDE.md "Engineering Quality §1".
          const _exhaustive: never = r;
          return _exhaustive;
        }
      }
    })
    .join('\n');
}

export function RecipeEditorCard({ dish }: Props) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const toast = useToast();

  // Fetch the existing recipe + the ingredient catalog (for
  // dropdown rendering + auto-fill of `unit` from the ingredient).
  const recipeQuery = useQuery({
    queryKey: ['catalog-dishes', 'recipe', dish.id],
    queryFn: () => dishAPI.recipe(dish.id),
  });
  const ingredientsQuery = useQuery({
    queryKey: ['catalog-ingredients', 'list-all'],
    queryFn: () => ingredientAPI.list({ limit: CATALOG_LIST_CAP }),
  });

  // Detect ingredient-catalog overflow: backend caps list responses
  // at CATALOG_LIST_CAP, so a real catalog with more rows would be
  // silently truncated for the dropdown — operator could not pick
  // the missing ingredients. Surface a one-time toast so the gap is
  // loud (CLAUDE.md §3 — no silent fallback). M2 will swap this for
  // typeahead; this guard is the bridge until then.
  const overflowWarnedRef = useRef(false);
  const ingredientTotal = ingredientsQuery.data?.total;
  useEffect(() => {
    if (
      typeof ingredientTotal === 'number' &&
      ingredientTotal > CATALOG_LIST_CAP &&
      !overflowWarnedRef.current
    ) {
      overflowWarnedRef.current = true;
      toast.error(
        t('dish.recipe.catalogTruncatedTitle'),
        t('dish.recipe.catalogTruncatedDetail', {
          shown: CATALOG_LIST_CAP,
          total: ingredientTotal,
        }),
      );
    }
  }, [ingredientTotal, toast, t]);

  const ingredientById = useMemo(() => {
    const map = new Map<string, Ingredient>();
    for (const ing of ingredientsQuery.data?.items ?? []) {
      map.set(ing.id, ing);
    }
    return map;
  }, [ingredientsQuery.data]);

  const [rows, setRows] = useState<RecipeRow[]>([]);
  // Hydrate rows when we first see data for THIS dish.id. Keyed to
  // `dish.id` (not a single bool) so navigating between dishes
  // without unmount (the common case under react-router useParams)
  // re-hydrates rather than leaving the previous dish's rows on
  // screen. Subsequent refetches (window-focus, sibling-tab
  // invalidate) for the same dish do NOT re-hydrate — the local
  // row state is the operator's working copy. After a successful
  // save we clear the ref so the next refresh resyncs server-
  // canonical values (ids, unit normalisation).
  const hydratedForRef = useRef<string | null>(null);
  useEffect(() => {
    if (hydratedForRef.current === dish.id) return;
    if (recipeQuery.data?.lines) {
      hydratedForRef.current = dish.id;
      setRows(
        recipeQuery.data.lines.map((line) => ({
          clientId: nextClientId(),
          ingredient_id: line.ingredient_id,
          qty_per_portion: line.qty_per_portion,
          unit: line.unit,
          prep_note: line.prep_note ?? '',
        })),
      );
    }
  }, [dish.id, recipeQuery.data]);

  /**
   * Build the line payload from local rows. Filters out rows that
   * lack any of the three required fields (ingredient_id / qty /
   * unit). Returns BOTH the lines and the count of partially-filled
   * rows that got dropped, so the click handler can warn the
   * operator before persisting (CLAUDE.md §3 — no silent fallback;
   * dropping work without telling the operator is exactly the
   * pattern this rule forbids).
   */
  // Mirrors the narrowed `dishAPI.setRecipe` parameter type. `unit`
  // is `CanonicalUnit`, NOT `CanonicalUnit | ''`: the `!r.unit` filter
  // below narrows the row before push.
  type RecipeLinePayload = {
    ingredient_id: string;
    qty_per_portion: string;
    unit: CanonicalUnit;
    prep_note?: string;
  };

  function buildSavePayload(): { lines: RecipeLinePayload[]; skipped: number } {
    let skipped = 0;
    const lines: RecipeLinePayload[] = [];
    for (const r of rows) {
      if (!r.ingredient_id && !r.qty_per_portion && !r.unit && !r.prep_note) {
        // Fully-empty row (e.g. operator clicked Add but never
        // filled it) — silently drop, not a partial.
        continue;
      }
      if (!r.ingredient_id || !r.qty_per_portion || !r.unit) {
        skipped += 1;
        continue;
      }
      // After the `!r.unit` filter, `r.unit` cannot be `''` — narrow
      // explicitly so the push type-checks against `CanonicalUnit`.
      const unit: CanonicalUnit = r.unit;
      lines.push({
        ingredient_id: r.ingredient_id,
        qty_per_portion: r.qty_per_portion,
        unit,
        prep_note: r.prep_note || undefined,
      });
    }
    return { lines, skipped };
  }

  const saveMutation = useMutation({
    mutationFn: (lines: RecipeLinePayload[]) => dishAPI.setRecipe(dish.id, lines),
    onSuccess: () => {
      hydratedForRef.current = null;
      queryClient.invalidateQueries({ queryKey: ['catalog-dishes', 'recipe', dish.id] });
      toast.success(t('dish.recipe.saved'));
    },
    onError: (err) => {
      // Atomic pre-validate (issue #18) returns the ADR-0006 envelope
      // `{ error: { extras: { reasons: [...] } } }` at HTTP 422 —
      // `lib/api.ts` surfaces `error.extras` on `ApiError.extras`, so
      // read the reasons off `err.extras.reasons` (NOT the archived
      // backend's top-level `extras.invalid_lines`). Surface them
      // line-by-line so the operator sees which row failed why —
      // `resolveApiError` only handles the string-sentinel path
      // (`extras.error`) which this 422 does not emit.
      const invalidLines =
        err instanceof ApiError
          ? (err.extras as { reasons?: unknown } | undefined)?.reasons
          : undefined;
      if (Array.isArray(invalidLines) && invalidLines.length > 0) {
        const detail = formatInvalidLines(invalidLines as InvalidLineReason[], ingredientById, t);
        toast.error(t('dish.recipe.saveFailed'), detail);
        return;
      }
      toast.error(
        t('dish.recipe.saveFailed'),
        resolveApiError(err, i18n, { namespace: 'dish', fallbackKey: 'dish.recipe.saveFailed' }),
      );
    },
  });

  /**
   * Apply a key/value patch to one row. Pure — does not auto-fill.
   * Use `selectIngredient` for the ingredient-pick path.
   */
  function update(index: number, patch: Partial<Omit<RecipeRow, 'clientId'>>) {
    setRows((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], ...patch };
      return next;
    });
  }

  /**
   * Operator picked an ingredient. Set the id AND auto-fill the unit
   * from the ingredient master (operator overrides survive — the
   * unit field is editable after, and the next ingredient pick
   * re-applies the master unit). Splitting this from update() keeps
   * the auto-fill rule out of the generic patch path so a future
   * caller cannot accidentally trigger it via an empty patch.
   */
  function selectIngredient(index: number, ingredientId: string) {
    setRows((prev) => {
      const next = [...prev];
      const ing = ingredientId ? ingredientById.get(ingredientId) : undefined;
      next[index] = {
        ...next[index],
        ingredient_id: ingredientId,
        unit: ing ? ing.unit : '',
      };
      return next;
    });
  }

  function addRow() {
    setRows((prev) => [...prev, newRow()]);
  }

  function removeRow(index: number) {
    setRows((prev) => prev.filter((_, i) => i !== index));
  }

  // Re-entrancy guard for the save button. The button's
  // `disabled={saveMutation.isPending}` covers the visible window,
  // but between the click handler firing and React committing the
  // pending state, a determined double-click can fire two PUTs.
  // Refs are synchronous, so this gate locks the second click out
  // before the mutation even queues.
  const saveInFlightRef = useRef(false);
  function handleSaveClick() {
    if (saveInFlightRef.current || saveMutation.isPending) return;
    const { lines, skipped } = buildSavePayload();
    if (skipped > 0) {
      // Loud warn so the operator notices their incomplete rows
      // were dropped. Toast persists ~5s; they can dismiss + edit
      // before clicking Save again. Save still proceeds for the
      // valid lines because re-clicking the save button after
      // dismissing is the same gesture as confirming "yes I meant
      // to drop those".
      toast.warning(
        t('dish.recipe.savedWithSkippedTitle'),
        t('dish.recipe.savedWithSkippedDetail', { count: skipped }),
      );
    }
    saveInFlightRef.current = true;
    saveMutation.mutate(lines, {
      onSettled: () => {
        saveInFlightRef.current = false;
      },
    });
  }

  // Initial load failure: no data ever arrived. Render a full-card
  // error so the operator does not see "no recipe lines yet" (which
  // is the empty-state, not a fetch error). For a refetch error
  // AFTER initial success we keep the working UI (TanStack Query
  // returns isError=true with stale data) so unsaved local edits
  // survive — surface the failure via a small banner instead.
  const initialLoadFailed =
    (recipeQuery.isError && !recipeQuery.data) ||
    (ingredientsQuery.isError && !ingredientsQuery.data);
  if (initialLoadFailed) {
    const err = recipeQuery.error ?? ingredientsQuery.error;
    return (
      <Card data-testid="recipe-editor-card">
        <CardHeader>
          <CardTitle>{t('dish.recipe.title')}</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-destructive" role="alert">
            {resolveApiError(err, i18n, {
              namespace: 'dish',
              fallbackKey: 'dish.recipe.loadFailed',
            })}
          </p>
        </CardContent>
      </Card>
    );
  }
  if (recipeQuery.isLoading || ingredientsQuery.isLoading) {
    return <Loading />;
  }

  const refetchFailed = recipeQuery.isError || ingredientsQuery.isError;

  return (
    <Card data-testid="recipe-editor-card">
      <CardHeader>
        <CardTitle>{t('dish.recipe.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {refetchFailed && (
          <p className="text-xs text-destructive" role="alert" data-testid="recipe-refetch-error">
            {resolveApiError(recipeQuery.error ?? ingredientsQuery.error, i18n, {
              namespace: 'dish',
              fallbackKey: 'dish.recipe.loadFailed',
            })}
          </p>
        )}
        {rows.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t('dish.recipe.empty')}</p>
        ) : (
          rows.map((row, i) => (
            <div
              key={row.clientId}
              className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end"
              data-testid={`recipe-row-${i}`}
            >
              <div className="md:col-span-4">
                <label className="text-xs text-muted-foreground">
                  {t('dish.recipe.ingredient')}
                </label>
                <select
                  className="w-full border rounded px-2 py-1.5 text-sm bg-background"
                  value={row.ingredient_id}
                  onChange={(e) => selectIngredient(i, e.target.value)}
                >
                  <option value="">—</option>
                  {(ingredientsQuery.data?.items ?? []).map((ing) => (
                    <option key={ing.id} value={ing.id}>
                      {ing.name} ({t(`ingredient.enum.unit.${ing.unit}`)})
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-muted-foreground">{t('dish.recipe.qty')}</label>
                <Input
                  inputMode="decimal"
                  value={row.qty_per_portion}
                  onChange={(e) => update(i, { qty_per_portion: e.target.value })}
                  placeholder="0.00"
                />
              </div>
              <div className="md:col-span-2">
                <label className="text-xs text-muted-foreground">{t('dish.recipe.unit')}</label>
                <select
                  className="w-full border rounded px-2 py-1.5 text-sm bg-background"
                  value={row.unit}
                  onChange={(e) => update(i, { unit: e.target.value as CanonicalUnit | '' })}
                  data-testid={`recipe-unit-select-${i}`}
                >
                  <option value="">—</option>
                  {CANONICAL_UNITS.map((u) => (
                    <option key={u} value={u}>
                      {t(`ingredient.enum.unit.${u}`)}
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-3">
                <label className="text-xs text-muted-foreground">{t('dish.recipe.prepNote')}</label>
                <Input
                  value={row.prep_note}
                  onChange={(e) => update(i, { prep_note: e.target.value })}
                  placeholder={t('dish.recipe.prepNotePlaceholder')}
                />
              </div>
              <div className="md:col-span-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeRow(i)}
                  aria-label={t('ui.remove')}
                >
                  ✕
                </Button>
              </div>
            </div>
          ))
        )}
        <div className="flex gap-2 pt-2">
          <Button variant="outline" size="sm" onClick={addRow}>
            + {t('dish.recipe.addRow')}
          </Button>
          <Button size="sm" onClick={handleSaveClick} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? t('ui.saving') : t('dish.recipe.save')}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
